import type { AxisLocation, AxisTick } from '../../AxisAndGrid/Axis/Axis.types';
import type { UseChartSettingsResolvedProps } from '../../../hooks/useChartSettings/useChartSettings.types';
import type { AxisScaleType, AxisChartSettings, UseGeneratedAxisChartProps, ResolvedAxisChartSettings } from '../hooks/useGeneratedAxisChart.types';
import { generateLinearScale } from '../../../js/generators/generate-linear-scale/generateLinearScale';
import { generateTimeScale } from '../../../js/generators/generate-time-scale/generateTimeScale';
import { generatePointScale } from '../../../js/generators/generate-point-scale/generatePointScale';
import { toNumberTimestamp as timestamp } from '../../../util.js';
import { getMinMax, getMinMaxTime } from '../../../js/helpers/data-helpers/dataHelpers';
import type { Scale } from '../../../js/generators/scale.types';
import { TICK_COUNT } from '../../../js/generators/scaleMath';
import { getKeyItem } from '../../../js/default-handlers/check-if-selected/checkIfSelected';
import { getZonedMidnight } from '../../../js/helpers/date-helpers/dateHelpers';
const TIME_TICK_COUNT = 10;
const scalers = { linear: generateLinearScale, time: generateTimeScale, point: generatePointScale };

const identity = (value: unknown): string | undefined => {
  return typeof value === 'string' || (typeof value === 'number' && Number.isFinite(value))
    ? String(value) : undefined;
};


export const generateAxis = (props: UseGeneratedAxisChartProps, settings?: UseChartSettingsResolvedProps): ResolvedAxisChartSettings => {
  const { data = [] } = props;
  const { grouping_key } = props;
  const selected_rows = settings?.selected === undefined ? data : data.filter((row) =>
    settings.checkIfFiltered(row, 'legend', settings.selected, {
      ...props, handleZoom: undefined, grouping_key, selected: settings.selected,
      checkIfSelected: settings.checkIfSelected,
    }) !== true);

  const axes: ResolvedAxisChartSettings['axes'] = {};
  const prepared: {
    location: AxisLocation; axis: AxisChartSettings; vertical: boolean;
    scale_type: AxisScaleType; key: string; categorical: boolean; categories: string[];
    domain: (string | number | Date)[]; range: [number, number]; scale: Scale | null;
  }[] = [];
  for (const location of ['left', 'right', 'top', 'bottom'] as const) {
    const axis = props.axes?.[location];
    if (!axis) continue;
    const vertical = location === 'left' || location === 'right';
    const filtered = vertical ? props.filter_y ?? true : props.filter_x ?? false;
    const rows = filtered ? selected_rows : data;
    const { scale_type } = axis;
    const key = axis.value_key ?? axis.value_keys?.[0] ?? '';
    const axis_keys = axis.value_keys ?? (axis.value_key ? [axis.value_key] : []);
    const selected_keys = filtered && settings?.selected !== undefined
      ? axis_keys.filter((field) => settings.checkIfSelected(getKeyItem(field, grouping_key), 'legend', settings.selected,
        { grouping_key, selected: settings.selected }) === true)
      : [];
    const keys = selected_keys.length ? selected_keys : axis_keys;
    const values = axis.domain ?? rows.flatMap((row) => keys.map((field) => row[field]));
    const categorical = scale_type === 'point';
    let domain: (string | number | Date)[] = [];
    let categories: string[] = [];
    if (categorical) {
      categories = [...new Set(values.map(identity).filter((value): value is string => value !== undefined))];
      domain = categories;
    } else {
      const bound_rows = values.map(value => ({ axis_value: value }));
      const bounds = scale_type === 'time'
        ? getMinMaxTime({ data: bound_rows, timestamp_key: 'axis_value', min: axis.min, max: axis.max })
        : getMinMax({ data: bound_rows, value_keys: ['axis_value'], min: axis.min, max: axis.max });
      let minimum: number | undefined = bounds?.[0];
      let maximum: number | undefined = bounds?.[1];
      minimum ??= scale_type === 'time' ? timestamp(axis.min)
        : typeof axis.min === 'number' && Number.isFinite(axis.min) ? axis.min : undefined;
      maximum ??= scale_type === 'time' ? timestamp(axis.max)
        : typeof axis.max === 'number' && Number.isFinite(axis.max) ? axis.max : undefined;
      if (scale_type === 'linear' && minimum === 0 && maximum === 0) maximum = 1;
      if (minimum !== undefined && maximum !== undefined) {
        if (minimum > maximum) throw new RangeError('Axis min must not exceed max.');
        domain = scale_type === 'time' ? [new Date(minimum), new Date(maximum)] : [minimum, maximum];
      }
    }
    const range: [number, number] = axis.range ?? (vertical ? [100, 0] : [0, 100]);
    const scale = axis.scale ?? (axis.scaler
      ? axis.scaler({ domain, range, location })
      : scalers[scale_type]({ domain, range, nice: false }));
    if (!categorical && axis.tick_count !== undefined
      && (!Number.isSafeInteger(axis.tick_count) || axis.tick_count < 0)) {
      throw new RangeError('tick_count must be a nonnegative integer.');
    }
    prepared.push({ location, axis, vertical, scale_type, key, categorical, categories, domain, range, scale });
  }

  const paired_y = ['left', 'right'].every(location => prepared.some(side =>
    side.location === location && side.scale && !side.categorical && side.axis.tick_count !== 0));
  for (const { location, axis, vertical, scale_type, key, categorical, categories, domain, range, scale } of prepared) {
    const ticks: AxisTick[] = [];
    if (scale) {
      const positions = new Set<string>();
      let samples: { value: unknown; coordinate: number | undefined }[];
      if (categorical) {
        samples = categories.map((value) => ({ value, coordinate: scale(value) }));
      } else {
        const resolved_domain = scale.domain();
        const first = resolved_domain[0], last = resolved_domain[resolved_domain.length - 1];
        const equal_domain = resolved_domain.length > 0 && (first instanceof Date && last instanceof Date
          ? first.getTime() === last.getTime() : first === last);
        if (axis.tick_count === 0) samples = [];
        else if (equal_domain) samples = [{ value: first, coordinate: scale(first) }];
        else if (vertical && paired_y) {
          // Only paired Y axes share a fixed six-position grid.
          samples = [0, 20, 40, 60, 80, 100].map(coordinate => ({
            value: scale.invert(coordinate), coordinate,
          }));
        } else {
          const values = scale.ticks(axis.tick_count ?? (scale_type === 'time' ? TIME_TICK_COUNT : TICK_COUNT - 1));
          const is_daily = scale_type === 'time' && props.time_zone && values.length > 0
            && values.every(value => value instanceof Date && value.getTime() % 86400000 === 0);
          const domain_start = timestamp(first), domain_end = timestamp(last);
          const zoned_values = is_daily ? values.flatMap(value => {
            const midnight = getZonedMidnight(value as Date, props.time_zone);
            const time = midnight?.getTime();
            return midnight && time !== undefined && domain_start !== undefined && domain_end !== undefined
              && time >= Math.min(domain_start, domain_end) && time <= Math.max(domain_start, domain_end) ? [midnight] : [];
          }) : values;
          samples = zoned_values.map(value => ({ value, coordinate: scale(value) }));
        }
      }
      samples = samples.filter(({ value, coordinate }) =>
        coordinate !== undefined && Number.isFinite(coordinate)
        && (typeof value === 'string' || typeof value === 'number' || value instanceof Date)
        && (scale_type !== 'time' || timestamp(value) !== undefined));
      for (const { value, coordinate } of samples) {
        if (coordinate === undefined || !Number.isFinite(coordinate)) continue;
        const position = String(coordinate) + '%';
        if (positions.has(position)) continue;
        positions.add(position);
        if (!(typeof value === 'string' || typeof value === 'number' || value instanceof Date)) continue;
        ticks.push({
          value,
          unit: axis.unit ?? (scale_type === 'linear' ? props.unit : undefined),
          position,
        });
      }
    }
    const resolved_domain = scale?.domain() ?? domain;
    const continuous = !categorical && resolved_domain.length > 0;
    axes[location] = {
      ...axis,
      scale_type,
      scaler: axis.scaler ?? scalers[scale_type],
      ...(scale ? { scale } : {}),
      domain: resolved_domain,
      range: (scale?.range() as [number, number] | undefined) ?? range,
      ...(continuous ? {
        min: resolved_domain[0] as number | Date,
        max: resolved_domain[resolved_domain.length - 1] as number | Date,
      } : {}),
      label: axis.label !== undefined ? axis.label : key,
      ticks,
    };
  }

  return { axes };
};
